<template>
    <div class="subscription">
        <span style="display: none;">v3.5</span>
        
        <a class="card border-bottom no-decoration" role="button" href="#" @click.prevent="getBillingPortal()">
            <div class="card-section no-padding">
                <div class="thumb">
                    <h2 class="title">{{ subscription.name }}</h2>

                    <div class="price" v-if="subscription.price.value">
                        <span class="number lnum">
                            {{ subscription.price.value % 1 !== 0 ? subscription.price.value.toFixed(2) : subscription.price.value }}
                            <span class="currency">{{ subscription.price.currencySymbol }}</span>
                        </span>
                        <span class="period">{{ periodAbbr }}</span>
                    </div>
                    <div class="price" v-else>
                        <span class="font-size-body">{{ $t__('For Free') }}</span>
                    </div>
                </div>
            </div>
            <div class="card-section data">
                <span class="status"><i :class="['icon', statusClass]"></i></span>
                <span class="color-title">{{ statusText }}</span>
                <span class="action btn-link has-border small narrow">{{ $t__('Manage') }}</span>
            </div>
        </a>

        <modal v-if="showModal" @close="showModal = false" :initial-show="true" modal-width="750px">
            <template slot="custom-header">
                <div class="text">
                    <h3 class="margin-bottom-none color-white">{{ subscription.name }}</h3>
                </div>
            </template>

            <template slot="body" class="columns-gap-small">
                <strong class="block font-size-mini uppercase margin-bottom-half letter-spacing-medium color-title">{{
                    $t__('Description') }}</strong>

                <div class="description-container margin-bottom-1">
                    <ul class="description content">
                        <li v-for="line in description" :key="line.index" v-html="line"></li>
                    </ul>
                </div>

                <hr class="margin-bottom-form-group short">

                <div class="columns is-multiline is-spaced is-mobile">
                    <div class="column is-half-mobile is-half-tablet is-one-quarter-desktop">
                        <strong
                            class="block font-size-mini uppercase margin-bottom-half letter-spacing-medium color-title">{{
                            $t__('Status') }}</strong>
                        <span :class="{
                            'weight-bolder': !isActive || expired,
                            'color-attention-darker': !isActive,
                            'color-negative-darker': expired
                        }" v-text="statusText"></span>
                    </div>

                    <div class="column is-half-mobile is-half-tablet is-one-quarter-desktop">
                        <strong
                            class="block font-size-mini uppercase margin-bottom-half letter-spacing-medium color-title">{{
                            $t__('Price') }}</strong>

                        <span v-if="subscription.price.currencyPosition == 'left' && subscription.price.value"
                            v-text="subscription.price.currencySymbol + subscription.price.value.toFixed(2)"></span>
                        <span v-else-if="subscription.price.value"
                            v-text="subscription.price.value.toFixed(2) + subscription.price.currencySymbol"></span>
                        <span v-else>{{ $c__('price of subscription/plan', 'Free') }}</span>

                        <span class="font-size-mini color-text-lighter" v-html="translations.exclVat"
                            v-if="subscription.price.value"></span>
                    </div>

                    <div class="column is-half-mobile is-half-tablet is-one-quarter-desktop">
                        <strong
                            class="block font-size-mini uppercase margin-bottom-half letter-spacing-medium color-title">{{
                            $t__('Period') }}</strong>
                        {{ period }}
                    </div>

                    <div class="column is-half-mobile is-half-tablet is-one-quarter-desktop"
                        v-if="validUntil && subscription.price.value">
                        <strong
                            class="block font-size-mini uppercase margin-bottom-half letter-spacing-medium color-title">{{
                            validTitle }}</strong>
                        {{ validUntil }}
                    </div>
                </div>

                <hr class="margin-bottom-form-group" v-if="subscription.active && subscription.price.value">

                <div class="margin-top-1 margin-bottom-none" v-if="subscription.active && subscription.price.value">
                    <a class="btn textual grey-light align-left font-size-min billing-button" href="/account/billing">
                        <span class="btn-wrap space-between">
                            <span class="text margin-right-1" v-html="translations.renewHelp"></span>
                            <svg-icon name="wallet" classes="shrink-0"></svg-icon>
                        </span>
                    </a>
                </div>

                <height-transition>
                    <div class="notification negative margin-top-1" v-show="resumeError"
                        v-html="translations.requestError"></div>
                </height-transition>
            </template>

            <template slot="footer" v-if="isActive">
                <div class="columns-gap-mini">
                    <div class="columns is-multiline">
                        <div class="column is-half-tablet is-narrow-desktop">
                            <button class="btn medium-sm width-100 negative height-100" type="button"
                                @click.prevent="openCancelModal">{{ $t__('Cancel Subscription') }}</button>
                        </div>

                        <div class="column is-half-tablet is-narrow-desktop margin-left-auto">
                            <button
                                :class="['btn medium-sm positive width-100 height-100', { 'is-loading': manualRenewProcessing }]"
                                type="button" @click.prevent="renewSubscription">{{ $t__('Renew Now') }}</button>
                        </div>
                    </div>
                </div>
            </template>

            <template slot="footer" v-else>
                <button :class="['btn medium-sm positive narrow float-right', { 'is-loading': resumeProcessing }]"
                    type="button" @click.prevent="resumeSubscription">{{ $t__('Resume Subscription') }}</button>
            </template>
        </modal>

        <modal v-if="showCancelModal" @close="closeCancelModal" :initial-show="true" modal-width="650px">
            <template slot="body" class="columns-gap-small">
                <h3 class="weight-normal margin-bottom-1">{{ $t__('Cancel subscription?') }}</h3>

                <p v-html="translations.cancelInfo"></p>

                <div class="notification grey-light margin-bottom-none">
                    <div class="description-container">
                        <ul class="description font-size-min">
                            <li v-for="line in description" :key="line.index" v-html="line"></li>
                        </ul>
                    </div>
                </div>

                <height-transition>
                    <div class="notification negative margin-top-1" v-show="cancelError"
                        v-html="translations.requestError"></div>
                </height-transition>
            </template>

            <template slot="footer">
                <div class="columns-gap-mini">
                    <div class="columns is-multiline">
                        <div class="column is-half-tablet is-narrow-desktop">
                            <button class="btn grey-light medium-sm narrow width-100 height-100" type="button"
                                @click.prevent="closeCancelModal">{{ $t__('Keep Subscription') }}</button>
                        </div>

                        <div class="column is-half-tablet is-narrow-desktop margin-left-auto">
                            <button
                                :class="['btn medium-sm negative narrow width-100 height-100', { 'is-loading': cancelProcessing }]"
                                type="button" @click.prevent="cancelSubscription">{{ $t__('Cancel Subscription')
                                }}</button>
                        </div>
                    </div>
                </div>
            </template>
        </modal>

        <checkout-modal :plan-id="subscription.planId ? subscription.planId : 1" v-if="checkout" :initial-show="true"
            @success="subscriptionRenewed" @cancel="subscriptionRenewCanceled"></checkout-modal>
    </div>
</template>

<script>
import * as utils from '../support/utils';

import ApiClient from '../support/ApiClient';

export default {
    name: 'Subscription',

    props: {
        subscription: {
            // id: 1,
            // name: 'Site "bakery"',
            // description: 'Public website',
            // canceled: false,
            // autoRenewal: true,
            // to: '2018-08-18',
            // price: {
            //     value: '10.80',
            //     currencySymbol: '€'
            // },
            // period: 30
        }
    },

    data: () => ({
        isActive: false,
        validUntil: '',
        showModal: false,
        manualRenewProcessing: false,
        showCancelModal: false,
        cancelProcessing: false,
        cancelError: false,
        resumeProcessing: false,
        resumeError: false,
        translations: {},
        description: '',
        checkout: false,
        api: {}
    }),

    mounted() {
        this.validUntil = this.subscription.to;
        this.isActive = !this.subscription.canceled;

        this.translations = {
            renewHelp: this.$t__('You will be automatically charged according to your billing settings that you can check %shere%s.', '<u>', '</u>'),
            cancelInfo: this.$t__("Your subscription %s will be canceled and you won't be charged anymore. Everything included in subscription will expire on %s. After this date you won't be able to use it anymore. Here is a reminder what you will loose:", `<strong>'${this.subscription.name}'</strong>`, `<strong>'${this.validUntil}'</strong>`),
            exclVat: this.$t__('excl. %sVAT%s', '<span class=all-small-caps>', '</span>'),
            requestError: this.$t__('Sorry, something went wrong. Please, try again later or contact us on %ssupport@pixenio.com%s.', '<a href=mailto:support@pixenio.com>', '</a>'),
            planDescription: [
                this.$t__('Unlimited Pages, Blogs, Galleries'),
                this.$t__('Unlimited Storage and Bandwidth'),
                this.$t__('Mobile-optimized website'),
                this.$t__('SSL Certificate Included'),
                this.$t__('Customer Support')
            ]
        };

        this.description = !this.subscription.description ? this.translations.planDescription : this.subscription.description;

        if (this.hasSubscriptionHash() && this.subscription.id == this.getSubscriptionIdFromHash()) {
            this.showModal = true;

            if (utils.getURLParameter('renew')) {
                this.renewSubscription();
            }
        }

        this.api = new ApiClient('store');
    },

    computed: {
        statusText() {
            if (this.expired) return this.$t__('Expired');
            return this.isActive ? this.$t__('Active') : this.$t__('Expires soon');
        },

        statusClass() {
            if (this.expired) return 'off';
            return this.isActive ? 'on' : 'temp';
        },

        validTitle() {
            if (this.expired) return this.$t__('Expired');
            return this.isActive ? this.$t__('Next Payment') : this.$t__('Expires');
        },

        period() {
            switch (this.subscription.period) {
                case 30:
                    return this.$t__('Monthly')

                case 365:
                    return this.$t__('Annual')

                case null:
                    return this.$t__('Lifetime')

                default:
                    return ''
            }
        },

        periodAbbr() {
            return this.subscription.period == 30 ? this.$c__('monthly abbreviation or month in singular', 'mon.') : this.$c__('yearly abbreviation or year in singular', 'year.');
        },

        expired() {
            const validUntil = Date.parse(this.validUntil);
            let now = Date.now();
            return (now - validUntil) >= 0;
        }
    },

    methods: {
        getBillingPortal(period = null) {
            let endpoint = 'billing-portal';

            // If period is provided, add it as a query parameter
            if (period) {
                endpoint += `?period=${period}`;
            } else {
                // Extract period value from the period computed property
                const currentPeriod = this.subscription.period == 30 ? 'monthly' : 'yearly';
                endpoint += `?period=${currentPeriod}`;
            }

            this.api.get(endpoint, data => {
                if (data.url) {
                    window.location.href = data.url;
                }
            });
        },
        updateValidUntil() {
            var validUntil = this.expired ? new Date() : new Date(this.validUntil);

            if (this.subscription.period == 30) {
                validUntil.setMonth(validUntil.getMonth() + 1);
            } else {
                validUntil.setFullYear(validUntil.getFullYear() + 1);
            }

            this.validUntil = validUntil.toISOString().slice(0, 10);
            this.translations.cancelInfo = this.$t__("Your subscription %s will be canceled and you won't be charged anymore. Everything included in subscription will expire on %s. After this date you won't be able to use it anymore. Here is a reminder what you will loose:", `<strong>'${this.subscription.name}'</strong>`, `<strong>'${this.validUntil}'</strong>`)
        },

        openCancelModal() {
            this.showCancelModal = true;
        },

        closeCancelModal() {
            this.showCancelModal = false;
            this.cancelError = false;
        },

        renewSubscription() {
            this.manualRenewProcessing = true;
            this.checkout = true;
        },

        subscriptionRenewed() {
            this.isActive = true;
            this.updateValidUntil();
            this.manualRenewProcessing = false;
            this.showModal = false;
            this.checkout = false;
            this.$bus.$emit('toast', { message: this.$t__('Subscription renewed'), type: 'success' });
        },

        subscriptionRenewCanceled() {
            this.manualRenewProcessing = false;
            this.checkout = false;
        },

        cancelSubscription($event) {
            this.cancelProcessing = true;
            this.cancelError = false;

            this.api.patch(`subscription?id=${this.subscription.id}`,
                {
                    canceled: true
                },
                () => {
                    this.isActive = false;
                    this.cancelProcessing = false;
                    this.closeCancelModal();
                    this.showModal = false;
                    this.$bus.$emit('toast', { message: this.$t__('Subscription canceled'), type: 'success' })
                },
                () => {
                    setTimeout(() => {
                        this.cancelProcessing = false;
                        this.cancelError = true;
                    }, 300);
                }
            );
        },

        resumeSubscription() {
            this.resumeProcessing = true;
            if (!this.subscription.price.value || this.expired) {
                this.resumeProcessing = false;
                this.renewSubscription();
            } else {
                this.resumeError = false;
                this.api.patch(`subscription?id=${this.subscription.id}`,
                    {
                        canceled: false
                    },
                    () => {
                        this.isActive = true;
                        this.resumeProcessing = false;
                        this.showModal = false;
                        this.$bus.$emit('toast', { message: this.$t__('Subscription resumed'), type: 'success' })
                    },
                    () => {
                        setTimeout(() => {
                            this.resumeProcessing = false;
                            this.resumeError = true;
                        }, 300);
                    }
                );
            }
        },

        isBussy() {
            return this.manualRenewProcessing || this.cancelProcessing || this.resumeProcessing;
        },

        hasSubscriptionHash() {
            return window.location.hash ? window.location.hash.includes('subscription') : false;
        },

        getSubscriptionIdFromHash() {
            return /subscription-(\d+)$/g.exec(window.location.hash)[1];
        }
    }
};
</script>
